// client/src/routes/portal/documents.tsx — SCR-PORT-005 „Documente" · the portal's fifth screen.
//
// §8.16 registers four SCR-PORT-* screens and folds the tenant's documents into SCR-PORT-002;
// §6.6 gives the portal four tabs. This is a fifth of each, and the reason is the one D-1171
// recorded for the bell and D-1510 records for this: „Chiria mea" is the rent, and a tenant who
// wants the signed contract, the handover report or last winter's meter photograph is not asking
// a question about the rent. The documents are the same set either way — the contract's, and
// only the contract's (02 §9.4) — so nothing about WHAT a tenant can reach changes here; what
// changes is that reaching it is a tab rather than a scroll to the bottom of another screen.
//
// ONE SCREEN AND ONE ACTION. The row's only control is the download; there is no in-page
// viewer and therefore no `?document=` (D-1513). What IS in the URL is the page and, since
// D-2161, the segment — `?source=TENANT` for the renter's own submissions, absent for the
// landlord's papers — so a reload or a shared link comes back to the part of the archive that
// was being read rather than to its top (§9.2.3). Both are validated here and simply spread
// through `change` below.
//
// `?contract=` is the layout route's and is simply read: the documents shown are the ones filed
// against the contract in view, which is what the §6.6 header selector selects (D-1180).

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '@/components/layout/PageHeader';
import { ContractScope } from '@/features/portal/ContractScope';
import { PortalDocuments } from '@/features/portal/PortalDocuments';
import { PortalSkeleton } from '@/features/portal/PortalSkeletons';
import { portalDocumentsSearchSchema, type PortalDocumentsSearch } from '@/features/portal/search';

export const Route = createFileRoute('/portal/documents')({
  validateSearch: portalDocumentsSearchSchema,
  component: PortalDocumentsScreen,
});

function PortalDocumentsScreen() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  // `previous` is spread so `?contract=` — declared on `/portal`, not here — survives every one
  // of these navigations. A `search` object that named only this screen's own params would drop
  // the scope and send a two-lease tenant back to the other flat (D-1180).
  const change = (next: PortalDocumentsSearch) => {
    // `resetScroll: false` so the PAGER owns the scroll. `main.tsx` creates the
    // router with `scrollRestoration: true`, which jumps the page to the top the
    // instant a search param changes — measured at 216 px to 0 between two 50 ms
    // samples — and that jump beat the pager's smooth scroll every time. The router
    // no longer moves the page on these navigations; `PortalPager` animates it
    // instead, and a filter change simply leaves the reader where they were
    // (D-1674).
    void navigate({ search: (previous) => ({ ...previous, ...next }), resetScroll: false });
  };

  return (
    <>
      <PageHeader
        title={t('portal.documents.title')}
        description={t('portal.documents.subtitle')}
      />

      {/* The wait is the arrival's shape: the opening panel on its own washes with the four
          generated cards, then the archive's card (D-1682, D-3541). */}
      <ContractScope pending={<PortalSkeleton shape="documents" count={3} />}>
        {(contract) => (
          <PortalDocuments contract={contract} search={search} onSearchChange={change} />
        )}
      </ContractScope>
    </>
  );
}
